import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Snackbar,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Chip,
  Box,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import useMediaQuery from '@mui/material/useMediaQuery';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import WebIcon from '@mui/icons-material/Web';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RoomIcon from '@mui/icons-material/Room';
import GroupsIcon from '@mui/icons-material/Groups';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {
  getClubUsers,
  getTrainingRoomsByClub,
  updateClubFromFLTT,
  updateclub,
  uploadClubLogo,
} from './funtionsBD/Mainservice';
import TrainingRoomsMap from './TrainingRoomsMap';
import FlttMatchProposalDialog, {
  useFlttMatchReview,
} from './AdminLayout/FlttMatchProposalDialog';
import ClubLogoMark from './ClubLogoMark';
import { resolveClubImageUrl } from '../utils/resolveClubImageUrl';
import { fetchClubBrandPreview } from '../utils/fetchClubBrandPreview';
import { useBrand } from '../context/BrandContext';
import { canEditClubLogo } from '../utils/brandAccess';
import { validateClubLogoFile } from '../utils/clubLogoFile';
import {
  CLUB_COUNTRIES,
  clubCountryLabel,
  canUpdateClubFromFltt,
  normalizeClubCountry,
} from '../constants/countries';
import './ClubUsersModal.css';

const ROLE_FILTERS = [
  { id: 'all', labelKey: 'all' },
  { id: 'player', labelKey: 'player' },
  { id: 'coach', labelKey: 'coach' },
  { id: 'admin', labelKey: 'admin' },
];

const toDateInputValue = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildLocalClub = (source = {}) => ({
  club_id: source.club_id || null,
  club_name: source.club_name || '',
  club_email: source.club_email || '',
  club_website: source.club_website || '',
  phone_mobile: source.phone_mobile || '',
  founded_date: source.founded_date || null,
  club_address: source.club_address || '',
  postal_code: source.postal_code || '',
  city: source.city || '',
  country: source.country || '',
  club_description: source.club_description || '',
  club_lat: source.club_lat,
  club_lon: source.club_lon,
  club_status: source.club_status || 'active',
  club_image: source.club_image || null,
});

function trainingRoomMapsUrl(room) {
  const lat = parseFloat(room?.troom_lat);
  const lon = parseFloat(room?.troom_lon);
  if (Number.isFinite(lat) && Number.isFinite(lon)) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
  }
  const address = String(room?.troom_address || "").trim();
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

const ClubUsersModal = ({
  isOpen,
  onClose,
  clubId,
  clubName,
  clubImage,
  clubData,
  onClubUpdated,
  fitScreen = false,
  canOpenPresence = false,
  onOpenPresence,
}) => {
  const { t } = useTranslation();
  const { refreshBrand } = useBrand();
  const isNarrow = useMediaQuery('(max-width:767px)');
  const logoInputRef = useRef(null);

  const info = JSON.parse(localStorage.getItem('user') || '{}');

  const isSuperAdmin =
    info?.isSuperAdmin === true ||
    info?.type_user_id === 1 ||
    (info?.all_role_names &&
      Array.isArray(info.all_role_names) &&
      info.all_role_names.includes('Super Admin')) ||
    (info?.all_roles && Array.isArray(info.all_roles) && info.all_roles.includes('r1'));

  const isClubAdmin =
    info?.type_user_id === 2 ||
    (info?.all_role_names &&
      Array.isArray(info.all_role_names) &&
      info.all_role_names.includes('Admin'));
  const isCoach =
    info?.type_user_id === 3 ||
    (info?.all_role_names &&
      Array.isArray(info.all_role_names) &&
      (info.all_role_names.includes('Coach') || info.all_role_names.includes('Expert Coach')));
  const userClubId = info?.club_id;

  const clubIdMatches =
    userClubId === clubId ||
    clubData?.club_id === userClubId ||
    clubData?.club_id === clubId ||
    (clubId && userClubId && clubId.toString() === userClubId.toString());

  const canEdit = isSuperAdmin || (isClubAdmin && clubIdMatches) || (isCoach && clubIdMatches);
  /** Club info fields: Super Admin or club Admin only (matches backend updateClub). */
  const canEditClubInfo = isSuperAdmin || (isClubAdmin && clubIdMatches);
  const canEditLogo = canEditClubLogo(info, clubId);
  /** Phone / email / website — staff only (admins and coaches). */
  const canViewContactInfo =
    isSuperAdmin ||
    isClubAdmin ||
    isCoach ||
    info?.hasAdminRole === true ||
    info?.hasCoachRole === true;

  const [users, setUsers] = useState([]);
  const [trainingRooms, setTrainingRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [clubInfo, setClubInfo] = useState(null);
  const [localClub, setLocalClub] = useState(() => buildLocalClub(clubData || {}));
  const [editForm, setEditForm] = useState(null);
  const [editingClubInfo, setEditingClubInfo] = useState(false);
  const [savingClubInfo, setSavingClubInfo] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [clubBrandLogoUrl, setClubBrandLogoUrl] = useState(null);

  const canUpdateFromFltt = canUpdateClubFromFltt(
    info,
    localClub?.country || clubInfo?.country || clubData?.country,
    clubId || localClub?.club_id || clubData?.club_id
  );
  const matchReview = useFlttMatchReview(clubId, Boolean(isOpen && clubId), () => {
    fetchClubUsers();
  });

  const thisClubImage =
    logoPreviewUrl ||
    localClub?.club_image ||
    clubInfo?.club_image ||
    clubImage ||
    clubData?.club_image ||
    null;

  const resolvedClubLogoUrl = useMemo(
    () =>
      resolveClubImageUrl(thisClubImage) ||
      resolveClubImageUrl(clubBrandLogoUrl) ||
      null,
    [thisClubImage, clubBrandLogoUrl]
  );

  const displayClubName =
    (editingClubInfo ? editForm?.club_name : null) ||
    localClub?.club_name ||
    clubInfo?.club_name ||
    clubData?.club_name ||
    clubName ||
    t('club');
  const committee = clubInfo?.committee || [];

  useEffect(() => {
    if (isOpen && clubId) {
      setImageLoading(true);
      setActiveTab(0);
      setMemberSearch('');
      setRoleFilter('all');
      setEditingClubInfo(false);
      setEditForm(null);
      setLogoPreviewUrl(null);
      setClubBrandLogoUrl(null);
      setLocalClub(buildLocalClub(clubData || {}));
      fetchClubUsers();
      fetchTrainingRooms();
    }
  }, [isOpen, clubId]);

  useEffect(() => {
    if (!isOpen || editingClubInfo) return;
    if (clubData || clubInfo) {
      setLocalClub(buildLocalClub({ ...(clubData || {}), ...(clubInfo || {}) }));
    }
  }, [clubData, clubInfo, isOpen, editingClubInfo]);

  useEffect(() => {
    if (!isOpen || !clubId) {
      setClubBrandLogoUrl(null);
      return undefined;
    }
    let cancelled = false;
    fetchClubBrandPreview(clubId, {
      club_id: clubId,
      club_name: clubName,
      club_image: clubImage || clubData?.club_image || null,
    })
      .then((nextBrand) => {
        if (cancelled) return;
        const url =
          resolveClubImageUrl(nextBrand?.logo_url) ||
          resolveClubImageUrl(nextBrand?.logo_light_url) ||
          null;
        setClubBrandLogoUrl(url);
      })
      .catch(() => {
        if (!cancelled) setClubBrandLogoUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, clubId, clubName, clubImage, clubData?.club_image]);

  useEffect(() => {
    if (!resolvedClubLogoUrl) {
      setImageLoading(false);
    }
  }, [resolvedClubLogoUrl]);

  const fetchClubUsers = async () => {
    try {
      setLoading(true);
      const response = await getClubUsers(clubId);
      setUsers(response.data.data.users || []);
      const nextClub = response.data.data.club || null;
      setClubInfo(nextClub);
      if (nextClub && !editingClubInfo) {
        setLocalClub(buildLocalClub({ ...(clubData || {}), ...nextClub }));
      }
    } catch (err) {
      console.error('Error fetching club users:', err);
      setError(err.message || 'Failed to fetch club users');
      setOpen2(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingRooms = async () => {
    try {
      const response = await getTrainingRoomsByClub(clubId);
      setTrainingRooms(response.data.data || []);
    } catch (err) {
      console.error('Error fetching training rooms:', err);
    }
  };

  const handleUpdateFromFLTT = async () => {
    if (!canUpdateFromFltt) {
      setError('Update from FLTT is only available for Luxembourg club admins.');
      setOpen2(true);
      return;
    }
    try {
      setUpdating(true);
      setError('');
      setSuccess('');

      const response = await updateClubFromFLTT(clubId, true);

      if (!response || !response.data) {
        setError('No response received from server. Please check server logs.');
        setOpen2(true);
        return;
      }

      if (response.data.status === 'success') {
        const message = response.data.message || t('clubDataUpdatedSuccessfully');
        const dataSource = response.data.data?.data_source;
        const scrapeFailed = response.data.data?.scrape_failed;
        const playersUpdated = response.data.data?.players_updated ?? 0;
        const isFreshScrape = dataSource === 'scraped' || dataSource === 'scraped_puppeteer';
        let sourcePart = isFreshScrape ? '🌐 Fresh data from FLTT' : '📄 Cached data';
        if (scrapeFailed) {
          sourcePart = '⚠️ Cached data (FLTT blocked live scrape)';
        }
        const playersPart =
          playersUpdated > 0 ? ` — ${playersUpdated} player ranking(s) updated` : '';
        const fullMessage = `${message} (Source: ${sourcePart}${playersPart})`;

        if (scrapeFailed) {
          setError(fullMessage);
          setOpen2(true);
        } else {
          setSuccess(fullMessage);
          setOpen(true);
        }
        const discovered = response.data?.data?.potential_matches || [];
        if (discovered.length) matchReview.present(discovered);
        await fetchClubUsers();
        await fetchTrainingRooms();
      } else {
        const errorMessage =
          response.data.message || response.data.error || t('failedToUpdateClubData');
        const errorDetails = response.data.data?.errorDetails || {};
        const suggestion = response.data.data?.suggestion || '';

        let fullErrorMessage = errorMessage;
        if (errorDetails.httpStatus) {
          fullErrorMessage += ` (HTTP ${errorDetails.httpStatus})`;
        }
        if (suggestion) {
          fullErrorMessage += ` ${suggestion}`;
        }

        setError(fullErrorMessage);
        setOpen2(true);
      }
    } catch (err) {
      console.error('Error updating club data:', err);

      let errorMessage = t('failedToUpdateClubData');

      if (err.response?.data) {
        const errorData = err.response.data;
        errorMessage = errorData.message || errorData.error || errorMessage;

        if (errorData.data?.errorDetails?.httpStatus) {
          errorMessage += ` (HTTP ${errorData.data.errorDetails.httpStatus})`;
        }
        if (errorData.data?.suggestion) {
          errorMessage += ` ${errorData.data.suggestion}`;
        }
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        errorMessage =
          'Cannot reach the backend server. Make sure the API is running and the port matches env-config.js (local: 3344, remote: 3030).';
      } else if (err.message) {
        errorMessage = `Network error: ${err.message}`;
      }

      setError(errorMessage);
      setOpen2(true);
    } finally {
      setUpdating(false);
    }
  };

  const startEditClubInfo = () => {
    setEditForm({
      club_name: localClub.club_name || '',
      founded_date: toDateInputValue(localClub.founded_date),
      phone_mobile: localClub.phone_mobile || '',
      club_email: localClub.club_email || '',
      club_website: localClub.club_website || '',
      country: normalizeClubCountry(localClub.country) || '',
    });
    setEditingClubInfo(true);
  };

  const cancelEditClubInfo = () => {
    setEditingClubInfo(false);
    setEditForm(null);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const syncLocalUserClubImage = (url) => {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      if (!stored || typeof stored !== 'object') return;
      const sameClub =
        stored.club_id != null &&
        clubId != null &&
        String(stored.club_id) === String(clubId);
      if (!sameClub) return;
      stored.club_image = url;
      if (stored.club_details && typeof stored.club_details === 'object') {
        stored.club_details = { ...stored.club_details, club_image: url };
      }
      if (stored.club && typeof stored.club === 'object') {
        stored.club = { ...stored.club, club_image: url };
      }
      localStorage.setItem('user', JSON.stringify(stored));
      window.dispatchEvent(new Event('smarttt-user-updated'));
    } catch {
      /* ignore */
    }
  };

  const handleClubLogoFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !canEditLogo || !clubId) return;

    const check = validateClubLogoFile(file);
    if (!check.ok) {
      setError(t(check.messageKey));
      setOpen2(true);
      return;
    }

    const preview = URL.createObjectURL(file);
    setLogoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return preview;
    });

    try {
      setUploadingLogo(true);
      setError('');
      const response = await uploadClubLogo(clubId, file);
      const url =
        response?.data?.data?.logo_url ||
        response?.data?.data?.club_image ||
        null;
      if (!url) {
        throw new Error(t('clubLogoUploadFailed'));
      }
      const nextLocal = buildLocalClub({ ...localClub, club_image: url });
      setLocalClub(nextLocal);
      setClubInfo((prev) => (prev ? { ...prev, club_image: url } : { club_image: url }));
      setLogoPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      syncLocalUserClubImage(url);
      if (typeof refreshBrand === 'function') {
        await refreshBrand();
      }
      if (typeof onClubUpdated === 'function') {
        onClubUpdated(nextLocal);
      }
      setSuccess(t('clubLogoUpdated'));
      setOpen(true);
    } catch (err) {
      console.error('Error uploading club logo:', err);
      setLogoPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setError(err?.message || t('clubLogoUploadFailed'));
      setOpen2(true);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveClubInfo = async () => {
    if (!editForm?.club_name?.trim()) {
      setError(t('require') || 'Club name is required');
      setOpen2(true);
      return;
    }

    try {
      setSavingClubInfo(true);
      setError('');
      setSuccess('');

      const response = await updateclub(
        null,
        {
          name: editForm.club_name.trim(),
          email: editForm.club_email.trim(),
          address: localClub.club_address || '',
          website: editForm.club_website.trim(),
          phone: editForm.phone_mobile.trim(),
          postalCode: localClub.postal_code || '',
          city: localClub.city || '',
          country: normalizeClubCountry(editForm.country) || undefined,
          description: localClub.club_description || '',
          foundedDate: editForm.founded_date || undefined,
          lat: localClub.club_lat,
          lon: localClub.club_lon,
        },
        clubId
      );

      const updated = response?.data?.data || {};
      const nextLocal = buildLocalClub({
        ...localClub,
        ...updated,
        club_name: updated.club_name ?? editForm.club_name.trim(),
        club_email: updated.club_email ?? editForm.club_email.trim(),
        club_website: updated.club_website ?? editForm.club_website.trim(),
        phone_mobile: updated.phone_mobile ?? editForm.phone_mobile.trim(),
        founded_date: updated.founded_date ?? editForm.founded_date ?? null,
        country: updated.country ?? normalizeClubCountry(editForm.country) ?? localClub.country,
      });

      setLocalClub(nextLocal);
      setClubInfo((prev) => (prev ? { ...prev, ...nextLocal } : nextLocal));
      setEditingClubInfo(false);
      setEditForm(null);
      setSuccess(t('clubUpdatedSuccessfully'));
      setOpen(true);
      if (typeof onClubUpdated === 'function') {
        onClubUpdated(nextLocal);
      }
    } catch (err) {
      console.error('Error updating club info:', err);
      setError(err?.message || t('errorUpdatingClub'));
      setOpen2(true);
    } finally {
      setSavingClubInfo(false);
    }
  };

  const handleClose = () => {
    setUsers([]);
    setClubInfo(null);
    setError('');
    setSuccess('');
    setImageLoading(true);
    setMemberSearch('');
    setRoleFilter('all');
    setEditingClubInfo(false);
    setEditForm(null);
    setLogoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    onClose();
  };

  const getRoleIcon = (user) => {
    if (user.isAdmin) return <AdminPanelSettingsIcon sx={{ fontSize: 14 }} />;
    if (user.isCoach) return <SchoolIcon sx={{ fontSize: 14 }} />;
    if (user.isPlayer) return <PersonIcon sx={{ fontSize: 14 }} />;
    return <PersonIcon sx={{ fontSize: 14 }} />;
  };

  const getRoleColor = (user) => {
    if (user.isAdmin) return 'primary';
    if (user.isCoach) return 'success';
    if (user.isPlayer) return 'warning';
    return 'default';
  };

  const getRoleLabel = (user) => {
    if (user.roles_display) return user.roles_display;
    if (user.all_roles && Array.isArray(user.all_roles) && user.all_roles.length > 0) {
      return user.all_roles.join(' + ');
    }
    if (user.isAdmin) return t('admin');
    if (user.isCoach) return t('coach');
    if (user.isPlayer) return t('player');
    return user.type || t('unknown');
  };

  const formatClubDate = (dateString) => {
    if (!dateString) return t('notAvailable');
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return t('notAvailable');
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return t('notAvailable');
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return t('notAvailable');
    return phone;
  };

  const getClubStatusColor = (status) => (status === 'active' ? 'success' : 'default');

  const getDisplayName = (user) => {
    if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`;
    return user.alias || t('noName');
  };

  const getInitials = (user) =>
    (user.first_name?.[0] || user.alias?.[0] || user.email?.[0] || 'U').toUpperCase();

  const classementTier = (value) => {
    if (!value) return 'other';
    const letter = String(value).trim().charAt(0).toUpperCase();
    return ['A', 'B', 'C', 'D'].includes(letter) ? letter : 'other';
  };

  const filteredUsers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    return users.filter((user) => {
      if (roleFilter === 'player' && !user.isPlayer) return false;
      if (roleFilter === 'coach' && !user.isCoach) return false;
      if (roleFilter === 'admin' && !user.isAdmin) return false;

      if (!q) return true;
      const haystack = [
        user.first_name,
        user.last_name,
        user.alias,
        user.email,
        getRoleLabel(user),
        user.player_data?.fltt_classement,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [users, memberSearch, roleFilter, t]);

  const websiteValue = editingClubInfo
    ? editForm?.club_website
    : localClub?.club_website || clubData?.club_website;
  const websiteUrl = websiteValue
    ? websiteValue.startsWith('http')
      ? websiteValue
      : `https://${websiteValue}`
    : null;
  const hasClubInfo = Boolean(localClub?.club_name || clubData || clubInfo);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        fullScreen={isNarrow}
        className="club-dlg club-dlg--fit"
        PaperProps={{ className: 'club-dlg-paper' }}
      >
        {/* Hero */}
        <div className="club-dlg__hero">
          <IconButton className="club-dlg__hero-close" onClick={handleClose} size="small" aria-label={t('close')}>
            <CloseIcon fontSize="small" />
          </IconButton>

          <div
            className={`club-dlg__logo-wrap${
              canEditLogo
                ? ' club-dlg__logo-wrap--editable'
                : resolvedClubLogoUrl
                  ? ' club-dlg__logo-wrap--clickable'
                  : ''
            }`}
            onClick={() => {
              if (canEditLogo) {
                if (!uploadingLogo) logoInputRef.current?.click();
                return;
              }
              if (resolvedClubLogoUrl) window.open(resolvedClubLogoUrl, '_blank');
            }}
            title={
              canEditLogo
                ? resolvedClubLogoUrl
                  ? t('replaceClubLogo')
                  : t('addClubLogo')
                : resolvedClubLogoUrl
                  ? t('clickToViewFullImage')
                  : t('clubLogo')
            }
            role={canEditLogo || resolvedClubLogoUrl ? 'button' : undefined}
            tabIndex={canEditLogo || resolvedClubLogoUrl ? 0 : undefined}
            aria-label={
              canEditLogo
                ? resolvedClubLogoUrl
                  ? t('replaceClubLogo')
                  : t('addClubLogo')
                : t('clubLogo')
            }
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              if (canEditLogo) {
                if (!uploadingLogo) logoInputRef.current?.click();
              } else if (resolvedClubLogoUrl) {
                window.open(resolvedClubLogoUrl, '_blank');
              }
            }}
          >
            {imageLoading && resolvedClubLogoUrl ? (
              <div className="club-dlg__logo-spinner">
                <CircularProgress size={22} thickness={4} />
              </div>
            ) : null}
            <ClubLogoMark
              club={localClub}
              clubImage={thisClubImage || clubBrandLogoUrl}
              clubName={displayClubName}
              size="lg"
              fallback="letter"
              onLoad={() => setImageLoading(false)}
              onError={() => setImageLoading(false)}
            />
            {canEditLogo ? (
              <>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleClubLogoFile}
                />
                <span className="club-dlg__logo-edit" aria-hidden="true">
                  {uploadingLogo ? (
                    <CircularProgress size={16} thickness={5} color="inherit" />
                  ) : (
                    <PhotoCameraIcon sx={{ fontSize: 16 }} />
                  )}
                  {resolvedClubLogoUrl ? t('replaceClubLogo') : t('addClubLogo')}
                </span>
              </>
            ) : null}
          </div>

          <div className="club-dlg__hero-text">
            <h2 className="club-dlg__club-name">{displayClubName}</h2>
            <div className="club-dlg__hero-meta">
              <Chip
                label={localClub?.club_status || clubData?.club_status || clubInfo?.club_status || 'active'}
                color={getClubStatusColor(localClub?.club_status || clubData?.club_status || clubInfo?.club_status || 'active')}
                size="small"
                sx={{ height: 22, fontWeight: 700, textTransform: 'capitalize' }}
              />
              <span className="club-dlg__stat-pill">
                <PersonIcon sx={{ fontSize: 14 }} />
                {users.length} {t('members')}
              </span>
              {committee.length > 0 && (
                <span className="club-dlg__stat-pill">
                  <GroupsIcon sx={{ fontSize: 14 }} />
                  {committee.length} {t('committeeMembers')}
                </span>
              )}
              {trainingRooms.length > 0 && (
                <span className="club-dlg__stat-pill club-dlg__stat-pill--muted">
                  <RoomIcon sx={{ fontSize: 14 }} />
                  {trainingRooms.length} {t('trainingRooms')}
                </span>
              )}
              {canOpenPresence && typeof onOpenPresence === 'function' ? (
                <button
                  type="button"
                  className="club-dlg__presence-btn"
                  onClick={onOpenPresence}
                >
                  <HowToRegIcon sx={{ fontSize: 16 }} />
                  {t('presenceReport')}
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          <Tabs
            className="club-dlg__tabs"
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="fullWidth"
          >
            <Tab
              label={t('clubInformation')}
              icon={<BusinessIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
            />
            <Tab
              label={`${t('members')} (${users.length})`}
              icon={<PersonIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <DialogContent className="club-dlg__body">
          {/* ── Club overview ─────────────────────────────────────── */}
          {activeTab === 0 && (
            <div className="club-dlg__pane">
              {hasClubInfo && (
                <section className="club-dlg__section">
                  <div className="club-dlg__section-head">
                    <h3 className="club-dlg__section-title">
                      <BusinessIcon />
                      {t('clubInformation')}
                    </h3>
                    {canEditClubInfo && !editingClubInfo && (
                      <button
                        type="button"
                        className="club-dlg__edit-btn"
                        onClick={startEditClubInfo}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                        {t('editDetails')}
                      </button>
                    )}
                    {canEditClubInfo && editingClubInfo && (
                      <div className="club-dlg__edit-actions">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={cancelEditClubInfo}
                          disabled={savingClubInfo}
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                        >
                          {t('cancel')}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSaveClubInfo}
                          disabled={savingClubInfo}
                          startIcon={
                            savingClubInfo ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : null
                          }
                          sx={{
                            textTransform: 'none',
                            fontWeight: 650,
                            borderRadius: '8px',
                            bgcolor: 'var(--brand-primary, #0877BC)',
                            '&:hover': { bgcolor: 'var(--brand-primary-dark, #065a8f)' },
                          }}
                        >
                          {savingClubInfo ? t('updating') : t('save')}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="club-dlg__info-grid">
                    <div className="club-dlg__card">
                      <p className="club-dlg__card-label">{t('basicInformation')}</p>
                      <div className="club-dlg__row">
                        <div className="club-dlg__row-icon">
                          <BusinessIcon />
                        </div>
                        <div className="club-dlg__row-body">
                          <span className="club-dlg__row-key">{t('clubName')}</span>
                          {editingClubInfo ? (
                            <input
                              className="club-dlg__input"
                              type="text"
                              value={editForm?.club_name || ''}
                              onChange={(e) => handleEditFormChange('club_name', e.target.value)}
                              maxLength={100}
                              autoFocus
                            />
                          ) : (
                            <span className="club-dlg__row-val">
                              {localClub.club_name || t('notAvailable')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="club-dlg__row">
                        <div className="club-dlg__row-icon">
                          <CalendarTodayIcon />
                        </div>
                        <div className="club-dlg__row-body">
                          <span className="club-dlg__row-key">{t('foundedDate')}</span>
                          {editingClubInfo ? (
                            <input
                              className="club-dlg__input"
                              type="date"
                              value={editForm?.founded_date || ''}
                              onChange={(e) => handleEditFormChange('founded_date', e.target.value)}
                              max={toDateInputValue(new Date().toISOString())}
                            />
                          ) : (
                            <span className="club-dlg__row-val">
                              {formatClubDate(localClub.founded_date)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="club-dlg__row">
                        <div className="club-dlg__row-icon">
                          <PublicIcon />
                        </div>
                        <div className="club-dlg__row-body">
                          <span className="club-dlg__row-key">{t('country')}</span>
                          {editingClubInfo ? (
                            <select
                              className="club-dlg__input"
                              value={editForm?.country || ''}
                              onChange={(e) => handleEditFormChange('country', e.target.value)}
                            >
                              <option value="">{t('selectCountry')}</option>
                              {editForm?.country &&
                                !CLUB_COUNTRIES.some((c) => c.code === editForm.country) && (
                                  <option value={editForm.country}>{editForm.country}</option>
                                )}
                              {CLUB_COUNTRIES.map((c) => (
                                <option key={c.code} value={c.code}>
                                  {c.name} ({c.code})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="club-dlg__row-val">
                              {clubCountryLabel(localClub.country) || t('notAvailable')}
                            </span>
                          )}
                        </div>
                      </div>
                      {clubInfo?.fltt_data_retrieved_at && (
                        <div className="club-dlg__row">
                          <div className="club-dlg__row-icon">
                            <RefreshIcon />
                          </div>
                          <div className="club-dlg__row-body">
                            <span className="club-dlg__row-key">
                              {t('flttDataRetrieved') || 'FLTT data retrieved'}
                            </span>
                            <span className="club-dlg__row-val">
                              {formatClubDate(clubInfo.fltt_data_retrieved_at)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {canViewContactInfo ? (
                    <div className="club-dlg__card">
                      <p className="club-dlg__card-label">{t('contactInformation')}</p>
                      <div className="club-dlg__row">
                        <div className="club-dlg__row-icon">
                          <PhoneIcon />
                        </div>
                        <div className="club-dlg__row-body">
                          <span className="club-dlg__row-key">{t('phone')}</span>
                          {editingClubInfo ? (
                            <input
                              className="club-dlg__input"
                              type="tel"
                              value={editForm?.phone_mobile || ''}
                              onChange={(e) => handleEditFormChange('phone_mobile', e.target.value)}
                            />
                          ) : (
                            <span className="club-dlg__row-val">
                              {formatPhoneNumber(localClub.phone_mobile)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="club-dlg__row">
                        <div className="club-dlg__row-icon">
                          <EmailIcon />
                        </div>
                        <div className="club-dlg__row-body">
                          <span className="club-dlg__row-key">{t('contactEmail')}</span>
                          {editingClubInfo ? (
                            <input
                              className="club-dlg__input"
                              type="email"
                              value={editForm?.club_email || ''}
                              onChange={(e) => handleEditFormChange('club_email', e.target.value)}
                            />
                          ) : (
                            <span className="club-dlg__row-val">
                              {localClub.club_email || t('notAvailable')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="club-dlg__row">
                        <div className="club-dlg__row-icon">
                          <WebIcon />
                        </div>
                        <div className="club-dlg__row-body">
                          <span className="club-dlg__row-key">{t('website')}</span>
                          {editingClubInfo ? (
                            <input
                              className="club-dlg__input"
                              type="url"
                              placeholder="https://"
                              value={editForm?.club_website || ''}
                              onChange={(e) => handleEditFormChange('club_website', e.target.value)}
                            />
                          ) : (
                            <span className="club-dlg__row-val">
                              {websiteUrl ? (
                                <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
                                  {localClub.club_website}
                                </a>
                              ) : (
                                t('notAvailable')
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    ) : null}
                  </div>
                </section>
              )}

              {committee.length > 0 && (
                <section className="club-dlg__section">
                  <h3 className="club-dlg__section-title">
                    <GroupsIcon />
                    {t('committeeMembers')} ({committee.length})
                  </h3>
                  <div className="club-dlg__committee">
                    {committee.map((member, index) => (
                      <article key={`${member.name}-${index}`} className="club-dlg__committee-card">
                        <div className="club-dlg__committee-avatar">
                          {(member.name?.[0] || '?').toUpperCase()}
                        </div>
                        <div>
                          <p className="club-dlg__committee-name">{member.name}</p>
                          <p className="club-dlg__committee-role">{member.role}</p>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              )}

              <section className="club-dlg__section">
                <h3 className="club-dlg__section-title">
                  <RoomIcon />
                  {t('trainingRooms')} ({trainingRooms.length})
                </h3>
                {trainingRooms.length === 0 ? (
                  <div className="club-dlg__empty">
                    <RoomIcon />
                    <p>{t('noTrainingRooms')}</p>
                  </div>
                ) : (
                  <div className="club-dlg__rooms">
                    {trainingRooms.map((room) => {
                      const mapsUrl = trainingRoomMapsUrl(room);
                      const address = String(room.troom_address || '').trim();
                      return (
                      <div key={room.troom_id} className="club-dlg__room">
                        <div className="club-dlg__room-icon">
                          <RoomIcon />
                        </div>
                        <div>
                          <p className="club-dlg__room-name">{room.troom_name}</p>
                          {address ? (
                            mapsUrl ? (
                              <a
                                className="club-dlg__room-addr club-dlg__room-addr--maps"
                                href={mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={t('openInGoogleMaps', 'Open in Google Maps')}
                              >
                                <LocationOnIcon sx={{ fontSize: 12, verticalAlign: 'text-top', mr: 0.5 }} />
                                {address}
                              </a>
                            ) : (
                              <p className="club-dlg__room-addr">
                                <LocationOnIcon sx={{ fontSize: 12, verticalAlign: 'text-top', mr: 0.5 }} />
                                {address}
                              </p>
                            )
                          ) : null}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </section>

              <section className="club-dlg__section">
                <h3 className="club-dlg__section-title">
                  <LocationOnIcon />
                  {t('map')}
                </h3>
                <div className="club-dlg__map">
                  <TrainingRoomsMap
                    trainingRooms={trainingRooms}
                    onGeocodeRoom={(roomId, geocodeData) => {
                      setTrainingRooms((prev) =>
                        prev.map((room) =>
                          room.troom_id === roomId
                            ? {
                                ...room,
                                troom_lat: geocodeData.troom_lat,
                                troom_lon: geocodeData.troom_lon,
                              }
                            : room
                        )
                      );
                    }}
                  />
                </div>
              </section>
            </div>
          )}

          {/* ── Members — compact list ────────────────────────────── */}
          {activeTab === 1 && (
            <div className="club-dlg__members-pane">
              {matchReview.proposals.length > 0 && (
                <div className="fltt-match-banner">
                  <p className="fltt-match-banner__text">
                    {t('flttMatchBanner', { defaultValue: 'Potential match to review' })}
                  </p>
                  <button
                    type="button"
                    className="fltt-match-banner__button"
                    onClick={() => matchReview.setOpen(true)}
                  >
                    {t('flttMatchReview', { defaultValue: 'Review' })}
                  </button>
                </div>
              )}
              <div className="club-dlg__members-toolbar">
                <label className="club-dlg__search">
                  <SearchIcon />
                  <input
                    type="search"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder={t('search') || 'Search…'}
                    aria-label={t('search')}
                  />
                </label>
                <div className="club-dlg__filters" role="group" aria-label={t('role')}>
                  {ROLE_FILTERS.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className={`club-dlg__filter${roleFilter === f.id ? ' club-dlg__filter--active' : ''}`}
                      onClick={() => setRoleFilter(f.id)}
                    >
                      {t(f.labelKey)}
                    </button>
                  ))}
                </div>
                <span className="club-dlg__members-count">
                  {filteredUsers.length}/{users.length}
                </span>
              </div>

              {loading ? (
                <div className="club-dlg__empty">
                  <CircularProgress size={28} sx={{ mb: 1 }} />
                  <p>{t('loadingUsers')}</p>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="club-dlg__empty">
                  <PersonIcon />
                  <p>
                    {users.length === 0
                      ? t('noUsersFoundInClub')
                      : t('noMatches', { defaultValue: 'No matches' })}
                  </p>
                </div>
              ) : (
                <div className="club-dlg__members-list">
                  <div className="club-dlg__members-head">
                    <span>{t('user')}</span>
                    <span>{t('role')}</span>
                    <span>{t('classement')}</span>
                    <span>{t('points')}</span>
                    <span>{t('status')}</span>
                  </div>
                  {filteredUsers.map((user, index) => {
                    const classement = user.player_data?.fltt_classement;
                    const rankingPts = user.player_data?.fltt_ranking_points;
                    const rolePreview =
                      user.all_roles && Array.isArray(user.all_roles) && user.all_roles.length > 1
                        ? user.all_roles[0]
                        : getRoleLabel(user);
                    const roleUserForChip =
                      user.all_roles && Array.isArray(user.all_roles) && user.all_roles.length > 1
                        ? {
                            isAdmin:
                              user.all_roles[0] === 'Admin' || user.all_roles[0] === 'Super Admin',
                            isCoach:
                              user.all_roles[0] === 'Coach' ||
                              user.all_roles[0] === 'Expert Coach',
                            isPlayer: user.all_roles[0] === 'Player',
                          }
                        : user;

                    return (
                      <div
                        key={user.user_id || user.player_data?.player_id || `user-${index}`}
                        className="club-dlg__member-row"
                        title={canEdit ? user.email || undefined : undefined}
                      >
                        <div className="club-dlg__member-identity">
                          <div className="club-dlg__member-avatar">{getInitials(user)}</div>
                          <div style={{ minWidth: 0 }}>
                            <div className="club-dlg__member-name">{getDisplayName(user)}</div>
                            {user.alias && user.first_name ? (
                              <div className="club-dlg__member-sub">@{user.alias}</div>
                            ) : canEdit && user.email ? (
                              <div className="club-dlg__member-sub">{user.email}</div>
                            ) : null}
                          </div>
                        </div>

                        <div>
                          <Chip
                            className="club-dlg__member-role"
                            icon={getRoleIcon(roleUserForChip)}
                            label={
                              user.all_roles?.length > 1
                                ? `${rolePreview}+${user.all_roles.length - 1}`
                                : rolePreview
                            }
                            color={getRoleColor(roleUserForChip)}
                            size="small"
                            variant="outlined"
                            title={getRoleLabel(user)}
                          />
                        </div>

                        <div>
                          {classement ? (
                            <span
                              className={`club-dlg__classement club-dlg__classement--${classementTier(classement)}`}
                            >
                              {classement}
                            </span>
                          ) : (
                            <span className="club-dlg__dash">—</span>
                          )}
                        </div>

                        <div>
                          {rankingPts != null && rankingPts !== '' ? (
                            <span className="club-dlg__pts">
                              {parseFloat(rankingPts).toFixed(0)}
                            </span>
                          ) : (
                            <span className="club-dlg__dash">—</span>
                          )}
                        </div>

                        <div>
                          <span
                            className={`club-dlg__status-dot${user.isActive ? ' club-dlg__status-dot--active' : ''}`}
                          >
                            {user.isActive ? t('active') : t('inactive')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions className="club-dlg__footer">
          <div className="club-dlg__footer-meta">
            {users.length} {users.length !== 1 ? t('users') : t('user')} {t('found')}
          </div>
          <div className="club-dlg__footer-actions">
            {canUpdateFromFltt && (
              <Button
                onClick={handleUpdateFromFLTT}
                variant="contained"
                disabled={updating}
                startIcon={updating ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 650,
                  borderRadius: '10px',
                  bgcolor: 'var(--brand-primary, #0877BC)',
                  '&:hover': { bgcolor: 'var(--brand-primary-dark, #065a8f)' },
                }}
              >
                {updating ? t('updating') : t('updateFromFLTT')}
              </Button>
            )}
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
            >
              {t('close')}
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      <FlttMatchProposalDialog
        open={matchReview.open}
        proposals={matchReview.proposals}
        busy={matchReview.busy}
        error={matchReview.error}
        onAccept={matchReview.accept}
        onReject={matchReview.reject}
        onLater={matchReview.later}
      />

      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert variant="filled" severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={open2}
        autoHideDuration={3000}
        onClose={() => setOpen2(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert variant="filled" severity="error">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ClubUsersModal;
